import { customEmailValidation } from "../../../utils/utils";
import { VALIDATION_MESSAGES } from "../constants/auth.constants";

/**
 * Validation props for `FormInput`/`PasswordField`
 * (`required`, `errorMessage`, `rules.validate`) — spread directly onto each
 * field, e.g. `<FormInput control={...} name="email" {...loginFormRules.email} />`.
 */
export interface FieldRuleProps {
  required: boolean;
  errorMessage: string;
  rules?: {
    validate?: (value: string) => string | boolean;
    /** Fields to re-validate when this one changes (react-hook-form `deps`). */
    deps?: string | string[];
  };
}

/**
 * Validation rules for the auth forms. These mirror the legacy screens'
 * validation exactly (required-only, plus the forgot-password email format
 * check) — see the plan's "Current rules only" decision.
 */

export const loginFormRules: { email: FieldRuleProps; password: FieldRuleProps } = {
  email: { required: true, errorMessage: VALIDATION_MESSAGES.emailRequired },
  password: { required: true, errorMessage: VALIDATION_MESSAGES.passwordRequired }
};

export const forgotPasswordFormRules: { email: FieldRuleProps } = {
  email: {
    required: true,
    errorMessage: VALIDATION_MESSAGES.emailRequired,
    rules: { validate: customEmailValidation }
  }
};

export const resetPasswordFormRules: {
  email: FieldRuleProps;
  oldPassword: FieldRuleProps;
  newPassword: FieldRuleProps;
  confirmPassword: (getNewPassword: () => string) => FieldRuleProps;
} = {
  email: { required: true, errorMessage: VALIDATION_MESSAGES.emailRequired },
  oldPassword: { required: true, errorMessage: VALIDATION_MESSAGES.oldPasswordRequired },
  // Re-validates `confirmPassword` so a stale mismatch error clears when the new password changes.
  newPassword: {
    required: true,
    errorMessage: VALIDATION_MESSAGES.newPasswordRequired,
    rules: { deps: "confirmPassword" }
  },
  confirmPassword: getNewPassword => ({
    required: true,
    errorMessage: VALIDATION_MESSAGES.confirmPasswordRequired,
    rules: {
      validate: (value: string) =>
        value === getNewPassword() || VALIDATION_MESSAGES.confirmPasswordMismatch
    }
  })
};
